import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { computeStreak } from '@/lib/utils'
import type { UserProfile } from '@/lib/database.types'

// ─── Resize image to 200×200 square via Canvas ────────────────────────────────

async function resizeImage(file: File | Blob, size: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')
      if (!ctx) { reject(new Error('Canvas context unavailable')); return }

      // Center-crop to square before scaling
      const s  = Math.min(img.width, img.height)
      const dx = (img.width - s) / 2
      const dy = (img.height - s) / 2
      ctx.drawImage(img, dx, dy, s, s, 0, 0, size, size)

      canvas.toBlob(
        blob => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
        'image/jpeg',
        0.85,
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Image load error'))
    }

    img.src = objectUrl
  })
}

// ─── Colonnes autorisées dans updateProfile (protection contre l'écriture de champs système) ──

const PROFILE_ALLOWED_FIELDS: string[] = [
  'first_name', 'last_name', 'avatar_url', 'daily_time', 'daily_reminder_time', 'onboarding_done', 'daily_prayer_enabled',
]

// ─── Projection explicite (fcm_token exclu du côté client) ────────────────────

const PROFILE_SELECT = 'id, user_id, first_name, last_name, avatar_url, daily_time, daily_reminder_time, streak_count, last_active_date, onboarding_done, daily_prayer_enabled, created_at'

// ─── Cache module-scope + persistance localStorage ───────────────────────────
// Le profil est cacheé à l'échelle du module pour éviter le skeleton/flash
// quand le composant Home est remonté. La persistance localStorage permet
// d'afficher immédiatement firstName + streak au lancement suivant de l'app,
// sans attendre Supabase. Stale-while-revalidate : la requête tourne en
// arrière-plan pour revalider les données.
const LS_PROFILE_KEY = 'selah_cache_profile'
const LS_PROFILE_TTL = 7 * 24 * 60 * 60 * 1000 // 7 jours

let cachedProfile: UserProfile | null = null
let cachedProfileFor: string | null = null // user.id

// ── Hydrate depuis localStorage au chargement du module ──
// (exécuté une fois à l'import, avant tout render React)
;(() => {
  try {
    const raw = localStorage.getItem(LS_PROFILE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as { profile: UserProfile; userId: string; timestamp: number }
    if (!parsed.profile || !parsed.userId || !parsed.timestamp) return
    if (Date.now() - parsed.timestamp > LS_PROFILE_TTL) return
    cachedProfile = parsed.profile
    cachedProfileFor = parsed.userId
  } catch { /* ignore */ }
})()

function persistProfile(profile: UserProfile | null, userId: string | null): void {
  try {
    if (profile && userId) {
      localStorage.setItem(LS_PROFILE_KEY, JSON.stringify({
        profile,
        userId,
        timestamp: Date.now(),
      }))
    } else {
      localStorage.removeItem(LS_PROFILE_KEY)
    }
  } catch { /* ignore */ }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useProfile() {
  const { user } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(
    () => (user && cachedProfileFor === user.id ? cachedProfile : null),
  )
  // loading=false si on a déjà un profil en cache pour cet utilisateur
  const [loading, setLoading] = useState(
    () => !(user && cachedProfileFor === user.id && cachedProfile !== null),
  )
  const [error, setError] = useState<string | null>(null)

  // ─── Chargement du profil ────────────────────────────────────────────────────

  const fetchProfile = useCallback(() => {
    if (!user) { setProfile(null); setLoading(false); cachedProfile = null; cachedProfileFor = null; persistProfile(null, null); return }

    // Si on a déjà un profil en cache valide, ne pas afficher le skeleton
    // (stale-while-revalidate : la requête tourne en arrière-plan)
    const hasFreshCache = cachedProfileFor === user.id && cachedProfile !== null
    if (!hasFreshCache) setLoading(true)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(supabase.from('user_profile') as any)
      .select(PROFILE_SELECT)
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data, error: err }: { data: UserProfile | null; error: { message: string } | null }) => {
        if (err) setError(err.message)
        else {
          setProfile(data)
          cachedProfile = data
          cachedProfileFor = user.id
          persistProfile(data, user.id)
        }
        setLoading(false)
      })
  }, [user])

  useEffect(() => { fetchProfile() }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── updateProfile ───────────────────────────────────────────────────────────

  const updateProfile = useCallback(
    async (updates: Partial<UserProfile>): Promise<UserProfile | null> => {
      if (!profile) return null
      setError(null)

      // Whitelist: n'autoriser que les champs UI (pas streak_count, last_active_date, fcm_token…)
      const safeUpdates = Object.fromEntries(
        Object.entries(updates).filter(([k]) => PROFILE_ALLOWED_FIELDS.includes(k)),
      ) as Partial<UserProfile>

      if (Object.keys(safeUpdates).length === 0) return profile

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error: err } = await (supabase.from('user_profile') as any)
        .update(safeUpdates)
        .eq('id', profile.id)
        .select(PROFILE_SELECT)
        .single()

      if (err) {
        setError(err.message)
        return null
      }

      setProfile(data)
      cachedProfile = data
      if (cachedProfileFor) persistProfile(data, cachedProfileFor)
      return data
    },
    [profile],
  )

  // ─── uploadAvatar ────────────────────────────────────────────────────────────
  /**
   * Redimensionne l'image en 200×200 côté client via Canvas,
   * upload dans le bucket Supabase Storage "avatars",
   * puis met à jour la colonne avatar_url du profil.
   *
   * Prérequis Supabase : créer le bucket "avatars" (public) dans Storage.
   */
  const uploadAvatar = useCallback(
    async (file: File | Blob): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Non connecté' }
      if (!file.type.startsWith('image/')) return { error: 'Veuillez sélectionner une image.' }
      if (file.size > 2_000_000) return { error: 'Fichier trop volumineux (max 2 Mo).' }

      try {
        // 1. Resize to 200×200 square JPEG
        const blob = await resizeImage(file, 200)

        // 2. Upload — path unique par utilisateur + timestamp (évite le cache navigateur)
        const path = `${user.id}/${Date.now()}.jpg`
        const { error: uploadError } = await Promise.race([
          supabase.storage.from('avatars').upload(path, blob, { upsert: true, contentType: 'image/jpeg' }),
          new Promise<{ error: { message: string } }>((_, reject) =>
            setTimeout(() => reject(new Error('Upload timeout — réessaie dans un instant.')), 15_000),
          ),
        ])

        if (uploadError) return { error: uploadError.message }

        // 3. Récupérer l'URL publique
        const { data: urlData } = supabase.storage
          .from('avatars')
          .getPublicUrl(path)

        // 4. Sauvegarder dans le profil
        await updateProfile({ avatar_url: urlData.publicUrl })
        return { error: null }
      } catch (err) {
        return { error: (err as Error).message }
      }
    },
    [user, updateProfile],
  )

  // ─── updateStreak ────────────────────────────────────────────────────────────
  /**
   * - Si last_active_date = aujourd'hui → rien ne change
   * - Si last_active_date = hier → streak_count + 1
   * - Si last_active_date < hier (ou null) → reset à 1
   * Toujours met last_active_date = today
   */
  const updateStreak = useCallback(async (): Promise<void> => {
    if (!import.meta.env.PROD) console.log('[Streak] updateStreak appelé - profile:', profile?.id ?? 'null')
    if (!profile) return

    const next = computeStreak(profile.streak_count ?? 0, profile.last_active_date)

    if (!import.meta.env.PROD) {
      console.log('[Streak] profile.last_active_date:', profile.last_active_date)
      console.log('[Streak] next:', JSON.stringify(next))
      console.log('[Streak] court-circuit ?', next.streak_count === profile.streak_count && next.last_active_date === profile.last_active_date)
    }

    // Pas de changement nécessaire (déjà actif aujourd'hui)
    if (
      next.streak_count === profile.streak_count &&
      next.last_active_date === profile.last_active_date
    ) {
      return
    }

    // Bypass updateProfile (whitelist) — streak_count et last_active_date sont système
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error: err } = await (supabase.from('user_profile') as any)
      .update({ streak_count: next.streak_count, last_active_date: next.last_active_date })
      .eq('id', profile.id)
      .eq('user_id', profile.user_id)
      .select(PROFILE_SELECT)
      .single()

    if (err) {
      if (!import.meta.env.PROD) console.error('[updateStreak] échec mise à jour streak:', err.message)
      return
    }
    if (data) {
      setProfile(data)
      cachedProfile = data
      if (cachedProfileFor) persistProfile(data, cachedProfileFor)
    }
  }, [profile])

  return { profile, loading, error, refetch: fetchProfile, updateProfile, updateStreak, uploadAvatar }
}
